import { useConsentStatus } from "./measurement/ConsentChoices";
import { captureUXOnce } from "./measurement/measurement";
import { reportMilestone, setTrackingScope } from "./api/tracking";
import { usePostHog } from "@posthog/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, MotionConfig, useReducedMotion } from "framer-motion";
import { FunnelShell } from "./components/FunnelShell";
import { StepRenderer, preloadStepRendererChunk } from "./components/StepRenderer";
import { BottomConfirmBar } from "./components/ui/BottomConfirmBar";
import { TopBar } from "./components/ui/TopBar";
import { getImagePreloadCandidatesForSources } from "./components/imageMeta";
import { computeSectionProgress, getFunnelSections } from "./funnel/flowData";
import { DEFAULT_FUNNEL_ID } from "./funnel/registry";
import { STORY_PAIR_REVEAL_KEY, type FunnelStep } from "./funnel/types";
import { getRecommendedStories, hasStoryRecommendationAnswers } from "./funnel/storyRecommendations";
import { resolveCheckoutPrefillFromUrl } from "./funnel/resolveCheckoutPrefillFromUrl";
import { resolveFunnelFromUrl, resolveVariantFromUrl } from "./funnel/resolveFunnelFromUrl";
import { replaceStepInUrl, resolveStepFromUrl } from "./funnel/resolveStepFromUrl";
import { preloadStoryMedia } from "./funnel/preloadStoryMedia";
import { useFunnelState } from "./funnel/useFunnelState";
import { useFunnelTracking } from "./funnel/useFunnelTracking";
import { useLivePrices } from "./funnel/useLivePrices";
import { usePaywallOffer } from "./funnel/usePaywallOffer";
import { buildCheckoutReturnUrl } from "./funnel/checkoutReturnUrl";
import { getPaywallPlans, isWinbackPromoCode, billingPlanId, planAmount } from "./funnel/paywallOffer";
import { buildFunnelImageRepository } from "./perf/funnelImageRepository";
import { startPhasedImagePreloadOnce } from "./perf/preloadImages";
import { getCheckoutImagePreloadUrls } from "./perf/checkoutImageAssets";

const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 32 : -32,
    opacity: 0,
    filter: "blur(6px)"
  }),
  center: {
    x: 0,
    opacity: 1,
    filter: "blur(0px)"
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -32 : 32,
    opacity: 0,
    filter: "blur(6px)"
  })
};

const pageTransition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1]
} as const;

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type ScheduledTaskHandle =
  | { type: "idle"; id: number }
  | { type: "timeout"; id: number };

type WindowWithStartupFlags = Window &
  typeof globalThis & {
    __PARABLES_LAST_PAGEVIEW_URL__?: string;
    __PARABLES_META_PAGEVIEW_CAPI_SENT__?: boolean;
    __PARABLES_META_PAGEVIEW_EVENT_ID__?: string;
    __PARABLES_WELCOME_SHELL_CONTINUE__?: boolean;
    __PARABLES_WELCOME_SHELL_CAROUSEL_TIMER__?: number;
    requestIdleCallback?: (callback: (deadline: IdleDeadlineLike) => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const scheduleIdleTask = (work: () => void, timeout = 1500): ScheduledTaskHandle => {
  const idleWindow = window as WindowWithStartupFlags;

  if (typeof idleWindow.requestIdleCallback === "function") {
    return {
      type: "idle",
      id: idleWindow.requestIdleCallback(
        (deadline) => {
          if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
            work();
            return;
          }

          work();
        },
        { timeout }
      )
    };
  }

  return {
    type: "timeout",
    id: window.setTimeout(work, timeout)
  };
};

const cancelScheduledTask = (task: ScheduledTaskHandle | null): void => {
  if (!task) {
    return;
  }

  if (task.type === "idle") {
    (window as WindowWithStartupFlags).cancelIdleCallback?.(task.id);
    return;
  }

  window.clearTimeout(task.id);
};

const getJourneyPillIndex = (step: FunnelStep): number | undefined => {
  if (step.id === "email_capture") {
    return 3;
  }

  if (step.kind !== "journey_feature") {
    return undefined;
  }

  switch (step.layout) {
    case "story_pair":
      return 0;
    case "chat_feature":
      return 1;
    case "daily_verse":
      return 2;
  }

  return undefined;
};

const preloadLateStepModules = (): Promise<unknown[]> => {
  return Promise.all([
    import("./api/checkout"),
    import("./api/leads"),
    import("./funnel/buildOnboardingResponses"),
    import("./analytics/metaPixel")
  ]);
};

const App = () => {
  const posthog = usePostHog();
  const consent = useConsentStatus();
  const quizRunId = useRef(crypto.randomUUID()).current;
  const funnel = useMemo(() => resolveFunnelFromUrl(), []);
  const flow = useMemo(() => resolveVariantFromUrl(window.location.href, funnel), [funnel]);
  const steps = useMemo(() => funnel.getSteps(flow), [flow, funnel]);
  const funnelSections = useMemo(() => getFunnelSections(steps), [steps]);
  const funnelStateScopeKey = useMemo(
    () => (funnel.id === DEFAULT_FUNNEL_ID ? flow : `${funnel.id}:${flow}`),
    [flow, funnel.id]
  );
  const initialStepId = useMemo(() => resolveStepFromUrl(), []);
  const checkoutPrefill = useMemo(() => resolveCheckoutPrefillFromUrl(), []);
  const paymentStepId = useMemo(
    () => steps.find((step) => step.kind === "payment")?.id ?? null,
    [steps]
  );
  const resolvedInitialStepId = useMemo(() => {
    // Only honor the prefill -> payment override when the URL doesn't already point at a specific
    // step. Otherwise a user landing on e.g. `?email=x&promo=Y#checkout_complete` after a successful
    // payment would get bounced back to the payment step on reload.
    if (!initialStepId && checkoutPrefill.email && checkoutPrefill.promoCode && paymentStepId) {
      return paymentStepId;
    }

    return initialStepId;
  }, [checkoutPrefill.email, checkoutPrefill.promoCode, initialStepId, paymentStepId]);
  const initialAnswers = useMemo(
    () => (checkoutPrefill.email ? { email: checkoutPrefill.email } : undefined),
    [checkoutPrefill.email]
  );
  const {
    answers,
    currentIndex,
    currentStep,
    direction,
    canGoBack,
    canContinue,
    goBack,
    goNext,
    goToIndex,
    setSingleAnswer,
    toggleMultiAnswer,
    setTextAnswer
  } = useFunnelState(steps, {
    flowKey: funnelStateScopeKey,
    initialStepId: resolvedInitialStepId,
    initialAnswers
  });
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const { plans: livePlans, isLoading: pricesLoading, error: pricesError, retry: retryPrices } = useLivePrices();
  const offer = usePaywallOffer(funnelStateScopeKey, isWinbackPromoCode(checkoutPrefill.promoCode));
  const reducedMotion = useReducedMotion();
  const [emailError, setEmailError] = useState<string | null>(null);
  const advancingRef = useRef(false);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { reportNavigation, reportButton, reportLeadLink } = useFunnelTracking({ totalSteps: steps.length });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormProps, setPaymentFormProps] = useState<{
    planId: string;
    flow: typeof flow;
    email?: string;
    promoCode?: string;
    currency?: string;
    value?: number;
    quotedMinor?: number;
    eventId?: string;
    funnelId?: string;
    fbp?: string;
    fbc?: string;
    onPaymentSuccess: (subscriptionId: string) => void;
    onReady?: () => void;
  } | undefined>(undefined);
  const [, setIsWelcomeHeroReady] = useState(true);
  const emailSubmittedRef = useRef<string | null>(null);
  const backgroundWarmupStartedRef = useRef(false);
  const isWelcomeStep = currentStep?.id === "welcome";

  // Inject live Stripe prices into the payment step for rendering.
  const resolvedCurrentStep = useMemo(() => {
    if (!currentStep || currentStep.kind !== "payment") return currentStep;
    return { ...currentStep, plans: livePlans };
  }, [currentStep, livePlans]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("body-welcome-step", isWelcomeStep);

    return () => {
      document.body.classList.remove("body-welcome-step");
    };
  }, [isWelcomeStep]);

  useEffect(() => {
    document.body.dataset.funnelTheme = funnel.theme.className;

    return () => {
      delete document.body.dataset.funnelTheme;
    };
  }, [funnel.theme.className]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const welcomeStep = steps[0];
      if (welcomeStep?.kind === "welcome") {
        startPhasedImagePreloadOnce({
          runKey: `funnel:${funnel.id}:variant:${flow}:welcome-hero`,
          critical: getImagePreloadCandidatesForSources(
            welcomeStep.heroSlides?.map((slide) => slide.image)
          ),
          secondary: [],
          concurrency: 1
        });

        const validationWarnings = buildFunnelImageRepository({
          steps,
          currentIndex: 0,
          lookaheadSteps: 0
        }).warnings;

        if (import.meta.env.DEV && validationWarnings.length > 0) {
          console.warn("[image-preload] validation warnings", validationWarnings);
        }
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [flow, funnel.id, steps]);

  useEffect(() => {
    if (!currentStep || currentStep.kind === "welcome") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const imageRepository = buildFunnelImageRepository({
        steps,
        currentIndex,
        lookaheadSteps: 1
      });

      startPhasedImagePreloadOnce({
        runKey: `funnel:${funnel.id}:variant:${flow}:step:${currentStep.id}`,
        critical: imageRepository.critical,
        secondary: imageRepository.secondary,
        concurrency: 2
      });

      if (import.meta.env.DEV && imageRepository.warnings.length > 0) {
        console.warn("[image-preload] validation warnings", imageRepository.warnings);
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, currentStep, flow, funnel.id, steps]);

  const checkoutPreloadTriggerIndex = useMemo(() => {
    const paymentStepIndex = steps.findIndex((step) => step.kind === "payment");
    if (paymentStepIndex < 0) {
      return -1;
    }
    return Math.max(paymentStepIndex - 2, 0);
  }, [steps]);

  useEffect(() => {
    if (checkoutPreloadTriggerIndex < 0 || currentIndex < checkoutPreloadTriggerIndex) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      startPhasedImagePreloadOnce({
        runKey: `funnel:${funnel.id}:variant:${flow}:checkout-images`,
        critical: getCheckoutImagePreloadUrls(),
        secondary: []
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [checkoutPreloadTriggerIndex, currentIndex, flow, funnel.id]);

  useEffect(() => {
    if (!hasStoryRecommendationAnswers(answers, funnel.id)) return;
    preloadStoryMedia(getRecommendedStories(answers, funnel.id));
  }, [answers, funnel.id]);

  useEffect(() => {
    const startupWindow = window as WindowWithStartupFlags;
    if (startupWindow.__PARABLES_WELCOME_SHELL_CAROUSEL_TIMER__) {
      window.clearInterval(startupWindow.__PARABLES_WELCOME_SHELL_CAROUSEL_TIMER__);
      startupWindow.__PARABLES_WELCOME_SHELL_CAROUSEL_TIMER__ = undefined;
    }

    document.documentElement.dataset.appReady = "true";
  }, []);

  useEffect(() => {
    const trackingWindow = window as WindowWithStartupFlags;
    const pageViewEventId = trackingWindow.__PARABLES_META_PAGEVIEW_EVENT_ID__?.trim();

    if (!pageViewEventId || trackingWindow.__PARABLES_META_PAGEVIEW_CAPI_SENT__) {
      return;
    }

    trackingWindow.__PARABLES_META_PAGEVIEW_CAPI_SENT__ = true;
    void import("./analytics/metaPixel")
      .then(({ sendMetaPageViewToConversionsApi }) => {
        sendMetaPageViewToConversionsApi(pageViewEventId);
      })
      .catch(() => {
        trackingWindow.__PARABLES_META_PAGEVIEW_CAPI_SENT__ = false;
      });
  }, []);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome") {
      return;
    }

    const startupWindow = window as WindowWithStartupFlags;
    if (!startupWindow.__PARABLES_WELCOME_SHELL_CONTINUE__) {
      return;
    }

    startupWindow.__PARABLES_WELCOME_SHELL_CONTINUE__ = false;
    const continueTimer = window.setTimeout(() => {
      goNext();
    }, 0);

    return () => {
      window.clearTimeout(continueTimer);
    };
  }, [currentStep, goNext]);

  useEffect(() => {
    if (!currentStep || currentStep.id !== "welcome" || backgroundWarmupStartedRef.current) {
      return;
    }

    backgroundWarmupStartedRef.current = true;

    const warmupStartIndex = Math.min(currentIndex + 1, steps.length - 1);
    const postWelcomeImageRepository = buildFunnelImageRepository({
      steps,
      currentIndex: warmupStartIndex,
      lookaheadSteps: 1
    });
    let secondWarmupTask: ScheduledTaskHandle | null = null;

    const firstWarmupTask = scheduleIdleTask(() => {
      void preloadStepRendererChunk(steps[currentIndex + 1]?.kind);

      startPhasedImagePreloadOnce({
        runKey: `funnel:${funnel.id}:variant:${flow}:next-step-images`,
        critical: postWelcomeImageRepository.critical,
        secondary: [],
        concurrency: 1
      });

      secondWarmupTask = scheduleIdleTask(() => {
        const stepKinds = Array.from(new Set(steps.slice(currentIndex + 1).map((step) => step.kind)));

        void Promise.all([
          preloadLateStepModules(),
          ...stepKinds.map((kind) => preloadStepRendererChunk(kind))
        ]);

        startPhasedImagePreloadOnce({
          runKey: `funnel:${funnel.id}:variant:${flow}:background-secondary`,
          critical: [],
          secondary: postWelcomeImageRepository.secondary,
          concurrency: 2
        });
      }, 2500);
    }, 1200);

    return () => {
      cancelScheduledTask(firstWarmupTask);
      cancelScheduledTask(secondWarmupTask);
    };
  }, [currentIndex, currentStep, flow, funnel.id, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    if (currentStep.kind !== "welcome") {
      setIsWelcomeHeroReady(true);
      return;
    }

    setIsWelcomeHeroReady(!currentStep.heroImage);
  }, [currentStep]);

  useEffect(() => {
    const nextStep = steps[currentIndex + 1];
    if (!nextStep) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      void preloadStepRendererChunk(nextStep.kind);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, steps]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    replaceStepInUrl(currentStep.id);

    if (consent !== "granted") return;
    const currentUrl = window.location.href;
    const trackingWindow = window as WindowWithStartupFlags;
    if (trackingWindow.__PARABLES_LAST_PAGEVIEW_URL__ === currentUrl) {
      return;
    }

    trackingWindow.__PARABLES_LAST_PAGEVIEW_URL__ = currentUrl;
    posthog.capture("$pageview", {
      $current_url: currentUrl,
      funnel_id: funnel.id,
      funnel_slug: funnel.slug,
      funnel_name: funnel.name,
      funnel_flow: flow,
      funnel_variant_id: flow,
      funnel_hash_route: window.location.hash || `#${currentStep.id}`,
      funnel_step_id: currentStep.id,
      funnel_step_index: currentIndex
    });
  }, [currentIndex, currentStep, flow, funnel.id, funnel.name, funnel.slug, posthog, consent]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }
    setTrackingScope(`${funnel.id}/${flow}`);
    const questionIndexes = steps.flatMap((step, index) => step.kind === "question" ? [index] : []);
    reportMilestone("entry");
    if (currentIndex === questionIndexes[0]) reportMilestone("quiz_started");
    if (currentIndex === questionIndexes[Math.floor(questionIndexes.length / 2)]) reportMilestone("quiz_midpoint");
    if (currentStep.kind === "result" || currentStep.id === "faith_path_ready") reportMilestone("results");
    if (currentStep.id === "story_1") reportMilestone("features");
    if (currentStep.kind === "email") reportMilestone("email");
    if (currentStep.kind === "payment") reportMilestone("paywall");
    const quizProperties = {run_id: quizRunId, quiz_id: funnel.id, funnel_id: funnel.id, funnel_variant_id: flow};
    if (currentStep.kind === 'question') {
      captureUXOnce('quiz_started', quizRunId, quizProperties);
      captureUXOnce('quiz_step_viewed', `${quizRunId}:${currentStep.id}`, {...quizProperties, step_id: currentStep.id, step_index: currentIndex, step_kind: currentStep.kind});
    }
    if (questionIndexes.length && currentIndex === questionIndexes[questionIndexes.length - 1] + 1) captureUXOnce('quiz_completed', quizRunId, quizProperties);
    if (currentStep.kind === 'payment') captureUXOnce('paywall_viewed', quizRunId, {...quizProperties, presentation_id: quizRunId, placement_id: funnel.id});
    reportNavigation(currentIndex);
  }, [currentIndex, currentStep, reportNavigation, steps, funnel.id, flow, consent, quizRunId]);

  useEffect(() => {
    const onBrowserNavigation = () => {
      const stepIdFromUrl = resolveStepFromUrl();
      if (!stepIdFromUrl) {
        return;
      }

      const nextIndex = steps.findIndex((step) => step.id === stepIdFromUrl);
      if (nextIndex >= 0) {
        goToIndex(nextIndex);
      }
    };

    window.addEventListener("popstate", onBrowserNavigation);
    window.addEventListener("hashchange", onBrowserNavigation);
    return () => {
      window.removeEventListener("popstate", onBrowserNavigation);
      window.removeEventListener("hashchange", onBrowserNavigation);
    };
  }, [goToIndex, steps]);

  useEffect(() => {
    const timer = setTimeout(() => { advancingRef.current = false; }, reducedMotion ? 0 : 650);
    return () => clearTimeout(timer);
  }, [currentStep?.id, reducedMotion]);

  useEffect(() => () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); }, []);

  if (!currentStep) {
    return null;
  }

  const sectionProgress = computeSectionProgress(currentIndex, funnelSections);
  const journeyPillIndex = getJourneyPillIndex(currentStep);
  const isPaymentStep = currentStep.kind === "payment";
  const isLoadingStep = currentStep.kind === "loading";
  const isCheckoutCompleteStep = currentStep.id === "checkout_complete";
  const isStoryRecommendationStep = currentStep.kind === "story_recommendation";
  const shouldShowBottomBar =
    !isLoadingStep &&
    !isPaymentStep &&
    !isCheckoutCompleteStep &&
    (!isStoryRecommendationStep || canContinue);
  const confirmLabelText = currentStep.confirmLabel ?? "Continue →";
  const welcomeFinePrint = currentStep.kind === "welcome" ? currentStep.finePrint : undefined;
  
  const confirmLabel = confirmLabelText;
  const confirmDisabled = !canContinue || isSubmittingEmail || isStartingCheckout;

  const handleConfirm = async () => {
    if (!canContinue || isSubmittingEmail || isStartingCheckout || advancingRef.current) {
      return;
    }

    if (currentStep.kind === "welcome") {
      // The welcome canvas and its button share this action; lock before awaiting the chunk.
      advancingRef.current = true;
      void import("./analytics/metaPixel").then(({ trackMetaViewContent }) => trackMetaViewContent()).catch(() => {});
    }

    if (currentStep.kind === "payment") {
      const selectedPlanId = answers[currentStep.answerKey];
      if (typeof selectedPlanId !== "string") {
        return;
      }

      const discounted = offer.visible && offer.reason !== "information";
      const selectedPlan = getPaywallPlans(livePlans, offer.visible, offer.reason).find((plan) => plan.id === selectedPlanId);
      if (!selectedPlan) {
        return;
      }

      const emailStepIndex = steps.findIndex(step => step.kind === "email" && step.answerKey === "email");
      const email = typeof answers.email === "string" ? answers.email.trim() : "";
      if (emailStepIndex >= 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        goToIndex(emailStepIndex);
        return;
      }

      const { getMetaTrackingContext, rememberPendingCheckoutTracking } = await import("./analytics/metaPixel");
      const trackingContext = getMetaTrackingContext();

      const planValue = planAmount(selectedPlan, discounted);
      const planCurrency = selectedPlan.currencyCode ?? "USD";

      setIsStartingCheckout(true);
      setCheckoutError(null);
      setPaymentFormProps({
        planId: billingPlanId(selectedPlan),
        flow,
        funnelId: funnel.id,
        ...(email ? { email } : {}),
        ...(discounted ? { promoCode: selectedPlan.offer?.promoCode } : {}),
        currency: planCurrency,
        value: planValue,
        quotedMinor: discounted ? selectedPlan.offer?.firstPeriodAmount : selectedPlan.unitAmount,
        eventId: trackingContext.eventId,
        ...(trackingContext.externalId ? { externalId: trackingContext.externalId } : {}),
        ...(trackingContext.fbp ? { fbp: trackingContext.fbp } : {}),
        ...(trackingContext.fbc ? { fbc: trackingContext.fbc } : {}),
        onReady: () => {
          setIsStartingCheckout(false);
        },
        onPaymentSuccess: (subscriptionId: string) => {
          rememberPendingCheckoutTracking({
            sessionId: subscriptionId,
            eventId: trackingContext.eventId,
            planId: billingPlanId(selectedPlan),
            value: planValue,
            currency: planCurrency
          });
          setIsStartingCheckout(false);
          // Navigate to checkout complete with subscription_id. Strip the prefill params (email,
          // promo) so a reload/refresh on the success page doesn't get rerouted back to the
          // payment step by the prefill override.
          window.location.assign(buildCheckoutReturnUrl(window.location.href, subscriptionId));
        }
      });
      setShowPaymentForm(true);
      reportButton("open_checkout");

      return;
    }

    if (currentStep.kind === "email") {
      const normalizedEmail = String(answers[currentStep.answerKey] ?? "").trim().toLowerCase();
      if (emailSubmittedRef.current === normalizedEmail) {
        advancingRef.current = true;
        goNext();
        return;
      }
      const email = normalizedEmail;
      setEmailError(null);
      setIsSubmittingEmail(true);
      try {
        const [{ buildOnboardingResponses }, { saveLead }, { getMetaTrackingContext, trackMetaLead }] = await Promise.all([
          import("./funnel/buildOnboardingResponses"),
          import("./api/leads"),
          import("./analytics/metaPixel")
        ]);
        const onboardingResponses = buildOnboardingResponses(steps, answers);
        const trackingContext = getMetaTrackingContext();
        const leadResult = await saveLead(email, onboardingResponses, {
          eventId: trackingContext.eventId,
          externalId: trackingContext.externalId,
          fbp: trackingContext.fbp,
          fbc: trackingContext.fbc
        });
        trackMetaLead(trackingContext.eventId);
        reportLeadLink(leadResult.leadId);
        emailSubmittedRef.current = email;
        setTextAnswer(currentStep.answerKey, email);
      } catch {
        setEmailError("We couldn't save your profile. Check your connection and try again.");
        return;
      } finally {
        setIsSubmittingEmail(false);
      }
      advancingRef.current = true;
      goNext();
      return;
    }

    if (
      currentStep.kind === "journey_feature" &&
      currentStep.layout === "story_pair" &&
      answers[STORY_PAIR_REVEAL_KEY] !== "true"
    ) {
      setSingleAnswer(STORY_PAIR_REVEAL_KEY, "true");
      return;
    }

    advancingRef.current = true;
    goNext();
  };

  const handleSelectSingle = (answerKey: string, optionId: string) => {
    if (advancingRef.current) return;
    setSingleAnswer(answerKey, optionId);
    const shouldAutoAdvance =
      currentStep.kind === "question" && !currentStep.optionFeedback;
    if (shouldAutoAdvance) {
      advancingRef.current = true;
      advanceTimerRef.current = setTimeout(goNext, 180);
    }
  };

  const handleSetText = (answerKey: string, value: string) => {
    setTextAnswer(answerKey, value);

    if (
      currentStep.kind === "payment" &&
      answerKey === currentStep.answerKey
    ) {
      reportButton(`select_plan:${value}`);

      if (
        showPaymentForm &&
        paymentFormProps &&
        value !== paymentFormProps.planId
      ) {
        setShowPaymentForm(false);
        setPaymentFormProps(undefined);
        setIsStartingCheckout(false);
      }
    }
  };

  const handleBack = () => {
    if (advancingRef.current) return;
    if (
      currentStep.kind === "journey_feature" &&
      currentStep.layout === "story_pair" &&
      answers[STORY_PAIR_REVEAL_KEY] === "true"
    ) {
      setSingleAnswer(STORY_PAIR_REVEAL_KEY, "");
      return;
    }

    advancingRef.current = true;
    goBack();
  };

  const clearCheckout = () => {
    setCheckoutError(null);
    setShowPaymentForm(false);
    setPaymentFormProps(undefined);
    setIsStartingCheckout(false);
  };
  const handleClosePaywall = () => {
    clearCheckout();
    if (showPaymentForm || offer.visible || offer.questionOpen) {
      offer.dismiss();
      reportButton("paywall_return_to_main");
    } else {
      offer.reveal();
      reportButton("paywall_exit_opened");
    }
  };
  const handleReturnToPlans = () => {
    clearCheckout();
    offer.dismiss();
    reportButton("paywall_return_to_main");
  };

  return (
    <MotionConfig reducedMotion="user">
    <div
      className={`app-frame app-frame-funnel-${funnel.theme.className} ${isWelcomeStep ? "app-frame-welcome" : ""} ${isPaymentStep ? "app-frame-payment" : ""}`}
      data-funnel-id={funnel.id}
      data-funnel-slug={funnel.slug}
      data-funnel-theme={funnel.theme.className}
      data-variant-id={flow}
      onClick={isWelcomeStep ? handleConfirm : undefined}
      style={isWelcomeStep ? { cursor: "pointer" } : undefined}
    >
      <FunnelShell
        isScrollable={isPaymentStep}
        topBar={
          isPaymentStep ? null : (
            <TopBar
              canGoBack={canGoBack && currentStep.id !== "checkout_complete"}
              onBack={handleBack}
              sections={funnelSections}
              sectionProgress={sectionProgress}
              isWelcomeStep={isWelcomeStep}
              progressVariant={journeyPillIndex === undefined ? "sections" : "journeyPills"}
              journeyPillIndex={journeyPillIndex}
              useWelcomeChrome
            />
          )
        }
        bottomBar={
          shouldShowBottomBar ? (
            <BottomConfirmBar
              label={confirmLabel}
              disabled={confirmDisabled}
              onClick={handleConfirm}
              note={welcomeFinePrint}
              className={isWelcomeStep ? "bottom-confirm-bar-welcome" : undefined}
            />
          ) : undefined
        }
      >
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={currentStep.id}
            className={`step-page ${isWelcomeStep ? "step-page-welcome" : ""}`}
            custom={direction}
            variants={reducedMotion ? { enter: { opacity: 1 }, center: { opacity: 1 }, exit: { opacity: 1 } } : pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={reducedMotion ? { duration: 0 } : pageTransition}
          >
            {currentStep.kind === "email" && emailError && <p className="funnel-save-error" role="alert">{emailError}</p>}
            <StepRenderer
              step={resolvedCurrentStep}
              funnelId={funnel.id}
              paywall={{
                exitQuestionOpen: offer.questionOpen,
                exitReason: offer.reason,
                onConfirmExitReason: (reason) => {
                  offer.commitReason(reason);
                  reportButton(`paywall_exit_reason:${reason}`);
                },
                checkoutError,
                onClosePaywall: handleClosePaywall,
                onReturnToPlans: handleReturnToPlans,
                offerVisible: offer.visible,
                pricesLoading, pricesError, onRetryPrices: retryPrices
              }}
              answers={answers}
              onSelectSingle={handleSelectSingle}
              onToggleMulti={toggleMultiAnswer}
              onSetText={handleSetText}
              onWelcomeFirstImageReadyChange={setIsWelcomeHeroReady}
              onLoadingContinue={goNext}
              onPaymentContinue={handleConfirm}
              isPaymentSubmitting={isStartingCheckout}
              showPaymentForm={showPaymentForm}
              paymentFormProps={paymentFormProps}
            />
          </motion.div>
        </AnimatePresence>
      </FunnelShell>
    </div>
    </MotionConfig>
  );
};

export default App;
